/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { omit, get } from 'lodash';
import styled from 'styled-components';
import {
    Form,
    Input,
    Icon,
    Loader,
    Message,
    Label,
    Container,
} from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import {
    FormLabel,
    Button,
    Divider,
    PageHeader,
} from '../../../../components';

import TimeSelect from './TimeSelect';
import StepSelect from './StepSelect';
import TimeFormatSelect from './TimeFormatSelect';

type GeneralSettingsProps = {
    intl: Object,
};

const intlMessages = defineMessages({
    salonNamePlaceholder: {
        id: 'pages.settings.generalSettings.salonName.placeholder',
        defaultMessage: 'Salon name',
    },
    salonNameError: {
        id: 'pages.settings.generalSettings.salonNameError',
        defaultMessage: 'Please enter salon name',
    },
    salonAddressPlaceholder: {
        id: 'pages.settings.generalSettings.salonAddress.placeholder',
        defaultMessage: 'Salon address',
    },
    salonAddressError: {
        id: 'pages.settings.generalSettings.salonAddressError',
        defaultMessage: 'Please enter salon address',
    },
    startAtPlaceholder: {
        id: 'pages.settings.generalSettings.startAt.placeholder',
        defaultMessage: 'Start At',
    },
    endAtPlaceholder: {
        id: 'pages.settings.generalSettings.endAt.placeholder',
        defaultMessage: 'End At',
    },
    stepPlaceholder: {
        id: 'pages.settings.generalSettings.step.placeholder',
        defaultMessage: 'Step',
    },
    formatPlaceholder: {
        id: 'pages.settings.generalSettings.format.placeholder',
        defaultMessage: 'Format',
    },
    nameExistingsError: {
        id: 'pages.settings.generalSettings.nameExistingsError',
        defaultMessage: 'Salon with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

// Number of minutes in 24 hours
const DAY_IN_MINUTES = 1440;

export const GET_SALON_QUERY = gql`
    query getSalon($salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    name
                    address
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                    }
                }
            }
        }
    }
`;

export const UPDATE_SALON_MUTATION = gql`
    mutation updateSalon($input: UpdateSalonInput!) {
        updateSalon(input: $input) {
            salon {
                id
                name
                address
                settings {
                    schedule {
                        startAt
                        endAt
                        step
                        format
                    }
                }
            }
        }
    }
`;

const ActionButton = styled(Button)`
    margin-left: 0.75em !important;
`;

function GeneralSettings({ intl }: GeneralSettingsProps) {
    const salonId = useSelector(({ user }) => user.get('salon'));

    const { loading, data } = useQuery(GET_SALON_QUERY, {
        variables: { salon: salonId },
        fetchPolicy: 'cache-and-network',
    });

    const salon = !loading && data && data.viewer ? data.viewer.salon : null;

    const buildInitialValues = salonData => {
        if (!salonData) return { name: '', address: '' };
        return {
            ...omit(salonData, ['__typename', 'id', 'settings']),
            settings: {
                schedule: {
                    ...omit(salonData.settings.schedule, ['__typename']),
                },
            },
        };
    };

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: buildInitialValues(salon) });

    useEffect(() => {
        if (salon) {
            reset(buildInitialValues(salon));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salon, reset]);

    // Watch schedule values for dependent selects
    const scheduleStep = watch('settings.schedule.step');
    const scheduleFormat = watch('settings.schedule.format');
    const scheduleStartAt = watch('settings.schedule.startAt');
    const scheduleEndAt = watch('settings.schedule.endAt');

    const [updateSalon] = useMutation(UPDATE_SALON_MUTATION);

    const onSubmit = async formData => {
        try {
            await updateSalon({
                variables: {
                    input: {
                        id: salonId,
                        ...formData,
                    },
                },
            });
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (
                    graphQLError.name === 'AlreadyExists' &&
                    get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
                ) {
                    setError('name', {
                        message: intl.formatMessage(intlMessages.nameExistingsError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    if (loading && !salon) {
        return <Loader size="big" active />;
    }

    return (
        <Container fluid>
            <PageHeader>
                <FormattedMessage
                    id="pages.settings.generalSettings"
                    defaultMessage="General Settings"
                />
            </PageHeader>
            <Divider />
            {errors.root && (
                <Message error>
                    <Icon name="warning sign" />
                    {errors.root.message}
                </Message>
            )}
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Field error={!!errors.name}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.settings.generalSettings.salonName"
                            defaultMessage="Salon name (or company)"
                        />
                    </FormLabel>
                    <Controller
                        name="name"
                        control={control}
                        rules={{
                            required: intl.formatMessage(intlMessages.salonNameError),
                        }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Input
                                {...field}
                                placeholder={intl.formatMessage(
                                    intlMessages.salonNamePlaceholder,
                                )}
                                fluid
                            />
                        )}
                    />
                    {errors.name && (
                        <Label color="red" pointing basic>
                            {errors.name.message}
                        </Label>
                    )}
                </Form.Field>
                <Form.Field error={!!errors.address}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.settings.generalSettings.salonAddress"
                            defaultMessage="Salon address (or company)"
                        />
                    </FormLabel>
                    <Controller
                        name="address"
                        control={control}
                        rules={{
                            required: intl.formatMessage(intlMessages.salonAddressError),
                        }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Input
                                {...field}
                                placeholder={intl.formatMessage(
                                    intlMessages.salonAddressPlaceholder,
                                )}
                                fluid
                            />
                        )}
                    />
                    {errors.address && (
                        <Label color="red" pointing basic>
                            {errors.address.message}
                        </Label>
                    )}
                </Form.Field>
                <Form.Field required>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.settings.generalSettings.startAt"
                                    defaultMessage="Start At"
                                />
                            </FormLabel>
                            <Controller
                                name="settings.schedule.startAt"
                                control={control}
                                render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                    <TimeSelect
                                        {...field}
                                        value={value}
                                        onChange={(e, data) => onChange(data.value)}
                                        placeholder={intl.formatMessage(
                                            intlMessages.startAtPlaceholder,
                                        )}
                                        startAt={0}
                                        endAt={scheduleEndAt || DAY_IN_MINUTES}
                                        step={scheduleStep || 30}
                                        formatTime={scheduleFormat || 'HH:mm'}
                                    />
                                )}
                            />
                        </Form.Field>
                        <Form.Field>
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.settings.generalSettings.endAt"
                                    defaultMessage="End At"
                                />
                            </FormLabel>
                            <Controller
                                name="settings.schedule.endAt"
                                control={control}
                                render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                    <TimeSelect
                                        {...field}
                                        value={value}
                                        onChange={(e, data) => onChange(data.value)}
                                        placeholder={intl.formatMessage(
                                            intlMessages.endAtPlaceholder,
                                        )}
                                        startAt={scheduleStartAt || 0}
                                        endAt={DAY_IN_MINUTES}
                                        step={scheduleStep || 30}
                                        formatTime={scheduleFormat || 'HH:mm'}
                                    />
                                )}
                            />
                        </Form.Field>
                        <Form.Field>
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.settings.generalSettings.step"
                                    defaultMessage="Step"
                                />
                            </FormLabel>
                            <Controller
                                name="settings.schedule.step"
                                control={control}
                                render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                    <StepSelect
                                        {...field}
                                        value={value}
                                        onChange={(e, data) => onChange(data.value)}
                                        placeholder={intl.formatMessage(
                                            intlMessages.stepPlaceholder,
                                        )}
                                    />
                                )}
                            />
                        </Form.Field>
                        <Form.Field>
                            <FormLabel>
                                <FormattedMessage
                                    id="pages.settings.generalSettings.format"
                                    defaultMessage="Format"
                                />
                            </FormLabel>
                            <Controller
                                name="settings.schedule.format"
                                control={control}
                                render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                    <TimeFormatSelect
                                        {...field}
                                        value={value}
                                        onChange={(e, data) => onChange(data.value)}
                                        placeholder={intl.formatMessage(
                                            intlMessages.formatPlaceholder,
                                        )}
                                    />
                                )}
                            />
                        </Form.Field>
                    </Form.Group>
                </Form.Field>
                <Divider />

                <ActionButton
                    onClick={e => {
                        e.preventDefault();
                        reset();
                    }}
                    disabled={isSubmitting || !isDirty}
                    floated="right"
                    basic
                >
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.settings.generalSettings.cancel"
                        defaultMessage="Cancel"
                    />
                </ActionButton>
                <ActionButton
                    onClick={e => {
                        e.preventDefault();
                        handleSubmit(onSubmit)();
                    }}
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    floated="right"
                    primary
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.settings.generalSettings.save"
                        defaultMessage="Save"
                    />
                </ActionButton>
            </Form>
        </Container>
    );
}

export default injectIntl(GeneralSettings);
